import { WeightUnit } from "@redotech/crdb-enums/product/weight-unit";
import { CSVFile, CSVRow, processCSV } from "@redotech/csv/csv-parser";
import { Currency } from "@redotech/money/currencies";
import { OrderImportField } from "@redotech/redo-fulfillment-common/import-schema";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import { CreateFulfillmentOrder } from "@redotech/redo-fulfillment-operations-common/create-fulfillment-order";
import { RedoShopifyCountryCode } from "@redotech/redo-inventory-common/fulfillment-country-code";
import { ProductInfo } from "@redotech/redo-product-common/product";
import { parallelForEach } from "@redotech/util/async-iterator";
import {
  collectParseResults,
  FieldToColumnMap,
  ParseResult,
} from "./validate-fulfillment-orders";

// CSV SCHEMA:
// Name*
// Street Address*
// Address 2
// City*
// State*
// Zip*
// Weight (lbs)
// Product SKU's 1
// Product Quantity 1
// Product SKU's 2
// Product Quantity 2
// Product SKU's 3
// Product Quantity 3
// Product SKU's 4
// Product Quantity 4
// Product SKU's 5
// Product Quantity 5
// Product SKU's 6
// Product Quantity 6
// Product SKU's 7
// Product Quantity 7
// Product SKU's 8
// Product Quantity 8
// Product SKU's 9
// Product Quantity 9
// Product SKU's 10
// Product Quantity 10
// Product SKU's 11
// Product Quantity 11
// Product SKU's 12
// Product Quantity 12
// Product SKU's 13
// Product Quantity 13
// Product SKU's 14
// Product Quantity 14
// Product SKU's 15
// Product Quantity 15
// Product SKU's 16
// Product Quantity 16
// Product SKU's 17
// Product Quantity 17
// Product SKU's 18
// Product Quantity 18
// Product SKU's 19
// Product Quantity 19
// Product SKU's 20
// Product Quantity 20
// Order Number
// Gift Message
// Internal Notes

const SKU_FETCH_CONCURRENCY = 5;

export async function parseFortyBoxesCsv(
  file: File,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<ParseResult> {
  const data: CSVFile = await processCSV(file);

  const uniqueSkus = new Set<string>();
  for (const row of data.rows) {
    for (let i = 1; i <= 20; i++) {
      const sku = row[`Product SKU's ${i}` as keyof typeof row]
        ?.toString()
        .trim();
      if (sku) {
        uniqueSkus.add(sku);
      }
    }
  }

  const skuCache = new Map<string, ProductInfo[]>();
  await parallelForEach(uniqueSkus, SKU_FETCH_CONCURRENCY, async (sku) => {
    try {
      const { products } = await searchProducts(sku);
      skuCache.set(sku, products);
    } catch {
      skuCache.set(sku, []);
    }
  });

  const cachedSearch = async (sku: string) => ({
    products: skuCache.get(sku) ?? [],
  });

  return collectParseResults(
    data.rows,
    async (row) => buildRow(row, cachedSearch),
    (row, i) =>
      row[FortyBoxesCsvColumns.ORDER_NUMBER]?.trim() || `Row ${i + 1}`,
  );
}

async function buildRow(
  row: CSVRow,
  searchProducts: (sku: string) => Promise<{ products: ProductInfo[] }>,
): Promise<CreateFulfillmentOrder> {
  const name = row[FortyBoxesCsvColumns.NAME] ?? "";
  const address1 = row[FortyBoxesCsvColumns.STREET_ADDRESS] ?? "";
  const address2 = row[FortyBoxesCsvColumns.ADDRESS_2] ?? "";
  const city = row[FortyBoxesCsvColumns.CITY] ?? "";
  const state = row[FortyBoxesCsvColumns.STATE] ?? "";
  const zip = row[FortyBoxesCsvColumns.ZIP] ?? "";
  const orderNumber = row[FortyBoxesCsvColumns.ORDER_NUMBER] ?? "";
  const giftMessage = row[FortyBoxesCsvColumns.GIFT_MESSAGE] ?? "";
  const internalNotes = row[FortyBoxesCsvColumns.INTERNAL_NOTES] ?? "";

  if (!name || !address1 || !city || !state || !zip) {
    throw new Error("Failed to parse row - missing required address fields");
  }

  // Parse products - up to 20 product/quantity pairs
  const items: FulfillmentOrderLineItem[] = [];
  for (let i = 1; i <= 20; i++) {
    const skuColumn = `Product SKU's ${i}` as keyof typeof row;
    const quantityColumn = `Product Quantity ${i}` as keyof typeof row;

    const sku = row[skuColumn]?.toString().trim();
    const quantityStr = row[quantityColumn]?.toString().trim();

    if (sku && quantityStr) {
      const { products } = await searchProducts(sku);
      const populatedItem = getPopulatedItemFromSku(sku, products);
      const quantity = parseInt(quantityStr, 10);

      if (quantity > 1000) {
        throw new Error("Failed to parse row - quantity too high");
      }

      if (!isNaN(quantity) && quantity > 0) {
        items.push({
          sku,
          quantity,
          unitPrice: {
            amount: populatedItem?.variant?.price ?? "0",
            currency: Currency.USD,
          },
          totalPrice: {
            amount: populatedItem?.variant?.price ?? "0",
            currency: Currency.USD,
          },
          imageUrl: populatedItem?.image?.url,
          variantId: populatedItem?.variant?.id ?? sku,
          productId: populatedItem?.id ?? sku,
          vendor: "40 Boxes",
          requiresShipping: true,
          externalId: sku,
          customAttributes: [],
          totalQuantity: quantity,
          unfulfilledQuantity: quantity,
          weight: {
            unit: WeightUnit.G,
            value: populatedItem?.variant?.weightGrams ?? 0,
          },
          lineItemId: sku,
          title: populatedItem?.name ?? sku,
          productTitle: populatedItem?.name ?? sku,
          variantTitle: populatedItem?.variant?.name ?? sku,
          unitDiscountedPrice: {
            amount: populatedItem?.variant?.price ?? "0",
            currency: Currency.USD,
          },
          totalDiscountedPrice: {
            amount: populatedItem?.variant?.price ?? "0",
            currency: Currency.USD,
          },
          totalTax: { amount: "0", currency: Currency.USD },
          hsCode: null,
        });
      }
    }
  }

  const data: CreateFulfillmentOrder = {
    address: {
      countryCode: RedoShopifyCountryCode.Us,
      name,
      address1,
      address2,
      city,
      province: state,
      zip,
    },
    orderInfo: {
      orderNumber,
      orderDate: new Date(),
      paidDate: new Date(),
      shippingPaid: { amount: "0", currency: Currency.USD },
      taxPaid: { amount: "0", currency: Currency.USD },
      totalPaid: { amount: "0", currency: Currency.USD },
      items,
      internalNote: internalNotes,
      noteToBuyer: giftMessage,
    },
  };

  return data;
}

export enum FortyBoxesCsvColumns {
  NAME = "Name*",
  STREET_ADDRESS = "Street Address*",
  ADDRESS_2 = "Address 2",
  CITY = "City*",
  STATE = "State*",
  ZIP = "Zip*",
  WEIGHT = "Weight (lbs)",
  ORDER_NUMBER = "Order Number",
  GIFT_MESSAGE = "Gift Message",
  INTERNAL_NOTES = "Internal Notes",
}

export const fortyBoxesColumnMap: FieldToColumnMap = {
  [OrderImportField.RECIPIENT_FULL_NAME]: FortyBoxesCsvColumns.NAME,
  [OrderImportField.ADDRESS_LINE_1]: FortyBoxesCsvColumns.STREET_ADDRESS,
  [OrderImportField.ADDRESS_LINE_2]: FortyBoxesCsvColumns.ADDRESS_2,
  [OrderImportField.CITY]: FortyBoxesCsvColumns.CITY,
  [OrderImportField.STATE]: FortyBoxesCsvColumns.STATE,
  [OrderImportField.POSTAL_CODE]: FortyBoxesCsvColumns.ZIP,
  [OrderImportField.ORDER_NUMBER]: FortyBoxesCsvColumns.ORDER_NUMBER,
  [OrderImportField.INTERNAL_NOTES]: FortyBoxesCsvColumns.INTERNAL_NOTES,
  [OrderImportField.ITEM_UNIT_PRICE]: "Product SKU's N",
};

const getPopulatedItemFromSku = (sku: string, products: ProductInfo[]) => {
  const matchedProduct = products.find((product) =>
    product.variants.some((variant) => variant.sku === sku),
  );
  const matchedVariant = matchedProduct?.variants.find(
    (variant) => variant.sku === sku,
  );
  return { ...matchedProduct, variant: matchedVariant };
};
